import { safeExternalUrl } from '../../../utils/safeExternalUrl';
import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, Select, Avatar } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined, ShopOutlined, StarFilled, DeleteOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';
import { COUNTRIES } from '../../../utils/countries';
import './Companies.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  .ant-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const Companies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: Number(searchParams.get('page')) || 1,
    pageSize: Number(searchParams.get('perPage')) || 10,
    total: 0,
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [modalVisible, setModalVisible] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [deleteForeverTarget, setDeleteForeverTarget] = useState<any>(null);
  const [deleteForeverConfirmText, setDeleteForeverConfirmText] = useState('');
  const [deleteForeverLoading, setDeleteForeverLoading] = useState(false);

  useEffect(() => {
    fetchCompanies();
    fetchSubscriptionPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, search]);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_ALL_SUBSCRIPTION_PLANS);
      setSubscriptionPlans(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        pageId: pagination.current.toString(),
        perPage: pagination.pageSize.toString(),
        ...(search && { search }),
      });

      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_GET_COMPANIES}?${params}`);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        // Handle both paginate utility format (itemsList) and direct array
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCompanies(items);
        setPagination(prev => ({
          ...prev,
          total: data.paginator?.totalCount || data.total || items.length,
        }));
      }
    } catch (error: any) {
      message.error('Failed to fetch companies');
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleCreate = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        merchantCompany: {
          name: values.companyName,
          tagline: values.tagline,
          companyEmail: values.companyEmail,
          countryCode: values.countryCode,
          logoUrl: values.logoUrl,
          websiteUrl: values.websiteUrl,
        },
        merchantAccount: {
          email: values.merchantEmail,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          countryCode: values.countryCode,
        },
        subscriptionPlanId: values.subscriptionPlanId,
        billingDate: values.billingDate || 1,
      };

      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_MERCHANT_WITH_COMPANY, payload);
      message.success('Company and merchant created successfully. Merchant will receive an email to complete their registration.');
      setModalVisible(false);
      form.resetFields();
      fetchCompanies();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create company');
    }
  };

  const handleDeleteForever = async () => {
    if (!deleteForeverTarget) return;
    try {
      setDeleteForeverLoading(true);
      await apiClient.delete(
        API_ENDPOINTS.ADMIN_DELETE_COMPANY_FOREVER.replace('{companyId}', deleteForeverTarget.id)
      );
      message.success('Company permanently deleted');
      setDeleteForeverTarget(null);
      setDeleteForeverConfirmText('');
      fetchCompanies();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to permanently delete company');
    } finally {
      setDeleteForeverLoading(false);
    }
  };

  const columns = [
    {
      title: 'Logo',
      key: 'logo',
      width: 80,
      render: (_: any, record: any) => (
        <Avatar
          src={record.logoUrl}
          icon={<ShopOutlined />}
          size={40}
          style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
        />
      ),
    },
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => (
        <Space>
          {name}
          {record.isDefault && (
            <Tag icon={<StarFilled />} color="gold" style={{ marginLeft: 8 }}>
              Default Merchant
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Tagline',
      dataIndex: 'tagline',
      key: 'tagline',
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'companyEmail',
      key: 'companyEmail',
    },
    {
      title: 'Country',
      dataIndex: 'countryCode',
      key: 'countryCode',
    },
    {
      title: 'Website',
      dataIndex: 'websiteUrl',
      key: 'websiteUrl',
      ellipsis: true,
      render: (url: string) => url ? <a href={safeExternalUrl(url)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>{url}</a> : '-',
    },
    {
      title: 'Webhook URL',
      dataIndex: 'webHookPostUrl',
      key: 'webHookPostUrl',
      ellipsis: true,
    },
    {
      title: 'Custodial Address',
      dataIndex: 'custodialEVMAddress',
      key: 'custodialEVMAddress',
      ellipsis: true,
      render: (address: string) => address ? `${address.substring(0, 10)}...${address.substring(address.length - 8)}` : '-',
    },
    {
      title: 'Gas Wallet',
      dataIndex: 'evmGasWalletAddress',
      key: 'evmGasWalletAddress',
      ellipsis: true,
      render: (address: string) => address ? (
        <span title={address} style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
          {address.substring(0, 10)}...
        </span>
      ) : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (!status) return '-';
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : status === 'DELETED' ? 'gray' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Company Status',
      dataIndex: 'merchantStatus',
      key: 'merchantStatus',
      render: (merchantStatus: string) => {
        if (!merchantStatus) return '-';
        const color = merchantStatus === 'ACTIVE' ? 'green' : merchantStatus === 'SUSPENDED' ? 'red' : merchantStatus === 'PENDING' ? 'orange' : 'default';
        return <Tag color={color}>{merchantStatus}</Tag>;
      },
    },
    {
      title: 'Watcher Sync',
      dataIndex: 'watcherSyncedAt',
      key: 'watcherSyncedAt',
      render: (date: string) => date ? (
        <Tag color="cyan" title={new Date(date).toLocaleString()}>
          ✓ {new Date(date).toLocaleDateString()}
        </Tag>
      ) : (
        <Tag color="default">Not synced</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/kivu/companies/${record.id}`)}
            size="small"
          >
            View
          </Button>
          {record.status === 'DELETED' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setDeleteForeverTarget(record);
                setDeleteForeverConfirmText('');
              }}
              size="small"
            >
              Delete Forever
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Companies
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Company
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
              Search
            </Text>
            <Input
              placeholder="Search by company name or email"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              onBlur={() => {
                const newParams = new URLSearchParams(searchParams);
                if (search) {
                  newParams.set('search', search);
                } else {
                  newParams.delete('search');
                }
                setSearchParams(newParams);
              }}
              style={{ maxWidth: 400 }}
            />
          </div>
        </Space>
      </FiltersContainer>

      <TableScrollWrapper>
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
          }}
          className="dark-table"
        >
          <Table
            columns={columns}
            dataSource={companies}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => `Total ${total} companies`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
                const newParams = new URLSearchParams(searchParams);
                newParams.set('page', page.toString());
                newParams.set('perPage', pageSize.toString());
                setSearchParams(newParams);
              },
            }}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </TableScrollWrapper>

      <Modal
        title="Create Company & Merchant"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Create"
        cancelText="Cancel"
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Typography.Title level={5} style={{ color: COLORS.textPrimary, marginBottom: 16 }}>
            Company Information
          </Typography.Title>
          <Form.Item
            name="companyName"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Company Name" />
          </Form.Item>
          <Form.Item
            name="tagline"
            label="Tagline"
          >
            <Input placeholder="Company Tagline" />
          </Form.Item>
          <Form.Item
            name="companyEmail"
            label="Company Email"
            rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
          >
            <Input placeholder="company@example.com" />
          </Form.Item>
          <Form.Item
            name="countryCode"
            label="Country"
            rules={[{ required: true, message: 'Please select country' }]}
          >
            <Select
              placeholder="Select Country"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) => {
                // Get the country name from the option's children or use the country data
                const country = COUNTRIES.find(c => c.code === option?.value);
                const countryName = country?.name || '';
                return countryName.toLowerCase().includes(input.toLowerCase());
              }}
              options={COUNTRIES.map(country => ({
                value: country.code,
                label: (
                  <Space>
                    <ReactCountryFlag countryCode={country.code} svg style={{ width: '1.5em', height: '1.5em' }} />
                    {country.name}
                  </Space>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="logoUrl"
            label="Logo URL"
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="websiteUrl"
            label="Website URL"
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Typography.Title level={5} style={{ color: COLORS.textPrimary, marginTop: 24, marginBottom: 16 }}>
            Subscription Plan
          </Typography.Title>
          <Form.Item
            name="subscriptionPlanId"
            label="Subscription Plan"
            rules={[{ required: true, message: 'Please select a subscription plan' }]}
          >
            <Select
              placeholder="Select Subscription Plan"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) => {
                const planName = option?.label || '';
                return planName.toLowerCase().includes(input.toLowerCase());
              }}
              options={subscriptionPlans.map(plan => ({
                value: plan.id,
                label: `${plan.displayName} - $${plan.monthlyPrice}/month`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="billingDate"
            label="Billing Date (Day of Month)"
            rules={[{ required: true, message: 'Please select billing date' }]}
            initialValue={1}
          >
            <Select placeholder="Select Billing Date">
              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                <Select.Option key={day} value={day}>
                  {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of each month
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Typography.Title level={5} style={{ color: COLORS.textPrimary, marginTop: 24, marginBottom: 16 }}>
            Merchant Account Information
          </Typography.Title>
          <div style={{ 
            padding: '12px 16px', 
            background: COLORS.bgPrimary, 
            border: `1px solid ${COLORS.border}`, 
            borderRadius: '8px',
            marginBottom: 20,
            color: COLORS.textSecondary,
            fontSize: '14px'
          }}>
            Merchant will get an email to complete their registration and set their own password.
          </div>
          <Form.Item
            name="merchantEmail"
            label="Merchant Email"
            rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
          >
            <Input placeholder="merchant@example.com" />
          </Form.Item>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="First Name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[{ required: true, message: 'Please enter phone number' }]}
          >
            <Input placeholder="Phone Number" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Delete company forever"
        open={!!deleteForeverTarget}
        onCancel={() => {
          setDeleteForeverTarget(null);
          setDeleteForeverConfirmText('');
        }}
        onOk={handleDeleteForever}
        okText="Delete Forever"
        okType="danger"
        okButtonProps={{
          disabled: deleteForeverConfirmText !== deleteForeverTarget?.name,
          loading: deleteForeverLoading,
        }}
        cancelText="Cancel"
      >
        <Typography.Paragraph>
          This permanently deletes <strong>{deleteForeverTarget?.name}</strong> and everything
          related to it &mdash; merchants, wallets, KYC/business verification, invoices,
          transaction statements, credit history, webhook logs, and pending invites. This
          cannot be undone.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Type <strong>{deleteForeverTarget?.name}</strong> to confirm.
        </Typography.Paragraph>
        <Input
          value={deleteForeverConfirmText}
          onChange={(e) => setDeleteForeverConfirmText(e.target.value)}
          placeholder={deleteForeverTarget?.name}
        />
      </Modal>
    </PageContainer>
  );
};

export default Companies;

